import { api } from "@/modules/api";
import { getBackendApiErrorMessage } from "@/modules/api/errorHandling";
import { AppStore } from "@/store";
import { runInAction } from "mobx";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { objectModule } from "@/modules/object";
import { uuidModule } from "@/modules/uuid";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { resolveNoteSuggestionBaseContent } from "@/store/note/helpers/note-suggestion-content";
import { truncateSuggestionDataForTracking } from "@/store/note/helpers/suggestionTracking";
import { NoteCleanupRefineSuggestionData } from "@/store/note/types";
import { UpsertNoteSuggestionOperation } from "@/store/sync/operations/notes/UpsertNoteSuggestionOperation";
import { noteModule } from "@mem-labs/common-editor";

export const refineNote = async ({
  noteId,
  userPrompt,
  sessionId,
  activeSuggestionMarkdownContent,
  store,
}: {
  noteId: string;
  userPrompt: string;
  sessionId: string;
  activeSuggestionMarkdownContent?: string;
  store: AppStore;
}) => {
  const startTime = Date.now();

  try {
    const note = await store.notes.getAsync(noteId);
    if (!note) return { success: false, error: "Note not found" };
    runInAction(() => (note.isProcessingSuggestion = true));

    try {
      const {
        encodedContent: prevEncodedContent,
        markdownContent: prevMarkdownContent,
        primaryLabel: prevPrimaryLabel,
        secondaryLabel: prevSecondaryLabel,
      } = await resolveNoteSuggestionBaseContent(note);
      const targetRefineMarkdownContent = activeSuggestionMarkdownContent ?? prevMarkdownContent;
      const refineRequestId = uuidModule.generate();

      const response = await api.post("/v2/notes/{note_id}/refine/process", {
        params: { path: { note_id: noteId } },
        body: {
          refine_request_id: refineRequestId,
          associated_session_id: sessionId,
          associated_note_markdown_content: targetRefineMarkdownContent,
          user_prompt: userPrompt,
        },
      });

      if (!response.response.ok) {
        throw new Error(getBackendApiErrorMessage(response.error) ?? "Unknown API error");
      }

      const suggestedMarkdownContent = response.data?.suggested_markdown_content;
      if (!suggestedMarkdownContent) {
        throw new Error("No suggested content returned from server");
      }

      const {
        suggestedEncodedContent,
        suggestedEncodedContentDiff,
        suggestedPrimaryLabel,
        suggestedSecondaryLabel,
      } = noteModule.buildSuggestionData({
        previousEncodedContent: prevEncodedContent,
        suggestedMarkdownContent,
      });

      await new UpsertNoteSuggestionOperation({
        store,
        payload: {
          suggestion_id: refineRequestId,
          note_id: noteId,
          kind: "NOTE_CLEANUP_REFINE_V1",
          prev_encoded_content: prevEncodedContent,
          prev_primary_label: prevPrimaryLabel,
          prev_secondary_label: prevSecondaryLabel,
          prev_markdown_content: prevMarkdownContent,
          suggested_encoded_content: suggestedEncodedContent,
          suggested_encoded_content_diff: suggestedEncodedContentDiff,
          suggested_primary_label: suggestedPrimaryLabel,
          suggested_secondary_label: suggestedSecondaryLabel,
          suggested_markdown_content: suggestedMarkdownContent,
          metadata: {
            session_id: sessionId,
            user_prompt: userPrompt,
          },
        },
      }).execute();

      const trackingSuggestionData: NoteCleanupRefineSuggestionData = {
        id: refineRequestId,
        status: "PENDING",
        kind: "NOTE_CLEANUP_REFINE_V1",
        prev_encoded_content: prevEncodedContent,
        prev_primary_label: prevPrimaryLabel,
        prev_secondary_label: prevSecondaryLabel,
        prev_markdown_content: prevMarkdownContent,
        suggested_encoded_content: suggestedEncodedContent,
        suggested_encoded_content_diff: suggestedEncodedContentDiff,
        suggested_primary_label: suggestedPrimaryLabel,
        suggested_secondary_label: suggestedSecondaryLabel,
        suggested_markdown_content: suggestedMarkdownContent,
        metadata: {
          session_id: sessionId,
          user_prompt: userPrompt,
        },
      };

      const endTime = Date.now();
      const elapsedTime = Math.floor((endTime - startTime) / 1000);

      const {
        previousMarkdownContents,
        suggestedMarkdownContents,
        userPrompt: trackedUserPrompt,
      } = truncateSuggestionDataForTracking(trackingSuggestionData);

      trackEvent(TrackedEvent.NoteCleanupRefine, {
        session_id: sessionId,
        note_id: noteId,
        elapsed_time: elapsedTime,
        previous_title: trackingSuggestionData.prev_primary_label,
        previous_markdown_contents: previousMarkdownContents,
        suggested_markdown_contents: suggestedMarkdownContents,
        suggested_title: trackingSuggestionData.suggested_primary_label,
        user_prompt: trackedUserPrompt,
      });

      return { success: true };
    } finally {
      runInAction(() => (note.isProcessingSuggestion = false));
    }
  } catch (e: unknown) {
    logger.error({
      message: "Failed to refine note",
      info: {
        error: objectModule.safeErrorAsJson(e as Error),
        noteId,
        userPrompt,
        sessionId,
        suggestionKind: "NOTE_CLEANUP_REFINE_V1",
      },
    });
    toastModule.triggerToast({ content: "Failed to refine note. Please try again." });
    return { success: false, error: e instanceof Error ? e.message : "Unknown error" };
  }
};
